import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  Pagination,
  Snackbar,
  TextField,
  Typography,
  CircularProgress,
  Tooltip,
} from "@mui/material";
import { DataGrid, getGridStringOperators } from "@mui/x-data-grid";
import React, { useEffect, useRef, useState } from "react";
import SearchIcon from "@mui/icons-material/Search";
import axios from "axios";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import CandidateDetails from "./candidateDetails";
import { styled } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";
import { generate_filter_condition } from "../utiles/filter";
import _debounce from "lodash/debounce";
import { onGlobalFliterFunc } from "../utiles/onFilterChange";
import AdvanceFilter from "./advanceFilter";
import Cookies from "js-cookie";
import PersonOffOutlinedIcon from "@mui/icons-material/PersonOffOutlined";

// ── Circular progress with % label ───────────────────────────────────────────
function CircularProgressWithLabel({ value }) {
  return (
    <Box sx={{ position: "relative", display: "inline-flex" }}>
      <CircularProgress
        variant="determinate"
        value={value}
        size={40}
        thickness={5}
        color={value <= 20 ? "warning" : value < 60 ? "primary" : "success"}
      />
      <Box
        sx={{
          top: 0,
          left: 0,
          bottom: 0,
          right: 0,
          position: "absolute",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Typography variant="caption" component="div" color="text.secondary">
          {`${Math.round(value)}%`}
        </Typography>
      </Box>
    </Box>
  );
}

// ── Columns ───────────────────────────────────────────────────────────────────
const columns = [
  { field: "id", headerName: "ID", width: 110, filterable: false },
  {
    field: "score",
    filterable: false,
    headerName: "Profile Match",
    width: 130,
    renderCell: (params) => {
      const score = params.row.score;
      if (score === null || score === undefined) return "NA";
      return <CircularProgressWithLabel value={Math.round(score * 100)} />;
    },
  },
  {
    field: "fullName",
    headerName: "Full Name",
    filterable: false,
    width: 200,
    renderCell: (params) => (
      <Typography
        variant="body2"
        sx={{
          textOverflow: "ellipsis",
          overflow: "hidden",
          whiteSpace: "nowrap",
        }}
        title={params.row.fullName}
      >
        {params.row.fullName || "NA"}
      </Typography>
    ),
  },
  {
    field: "entered_by",
    headerName: "Created By",
    width: 180,
    filterable: false,
    filterOperators: getGridStringOperators().filter((op) =>
      ["contains", "equals", "isAnyOf"].includes(op.value),
    ),
  },
  {
    field: "created_at",
    filterable: false,
    headerName: "Date Created",
    width: 180,
    renderCell: (params) =>
      new Date(params.row.created_at).toLocaleDateString("en-US"),
  },
  {
    field: "current_location",
    headerName: "Location",
    filterable: false,
    width: 150,
    renderCell: (params) => (
      <Typography
        variant="body2"
        sx={{
          textOverflow: "ellipsis",
          overflow: "hidden",
          whiteSpace: "nowrap",
        }}
        title={params.row.current_location}
      >
        {params.row.current_location || "NA"}
      </Typography>
    ),
  },
  {
    field: "experience",
    filterable: false,
    headerName: "Experience",
    width: 150,
    renderCell: (params) => {
      const years = params.row.year || 0;
      const months = params.row.month || 0;
      let result = "";
      if (years > 0) result += `${years} year${years > 1 ? "s" : ""}`;
      if (months > 0)
        result += `${result ? " " : ""}${months} month${months > 1 ? "s" : ""}`;
      return years === 0 ? "NA" : result || "NA";
    },
  },
  {
    field: "current_salary",
    filterable: false,
    headerName: "Current Salary",
    width: 150,
    renderCell: (params) => params.row.current_salary || "NA",
  },
  {
    field: "last_active",
    filterable: false,
    headerName: "Last Active on",
    width: 180,
    renderCell: (params) => params.row.last_active || "NA",
  },
];

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialogContent-root": { padding: theme.spacing(2) },
  "& .MuiDialogActions-root": { padding: theme.spacing(1) },
}));

const dataGridSx = {
  border: "1px solid rgba(115, 119, 145, 0.2)",
  borderRadius: "4px",
  height: "100%",
  display: "flex",
  flexDirection: "column",
  "& .MuiDataGrid-columnHeaderCheckbox .MuiDataGrid-columnHeaderTitleContainer":
    { display: "none" },
  "& .MuiDataGrid-columnHeaders": {
    backgroundColor: "#D9EAFB",
    borderRadius: "4px 4px 0 0",
    color: "#151D48",
    borderBottom: "none",
    flexShrink: 0,
  },
  "& .MuiDataGrid-columnHeader": {
    backgroundColor: "#D9EAFB",
    padding: "14px 24px",
  },
  "& .MuiDataGrid-columnHeaderTitle": { fontWeight: 600, fontSize: "14px" },
  "& .MuiDataGrid-row": {
    cursor: "pointer",
    fontSize: "13px",
    backgroundColor: "#fff",
    "&:hover": { backgroundColor: "rgb(245, 245, 245)" },
  },
  "& .MuiDataGrid-row.row-alternate": {
    backgroundColor: "#FBFBFB",
    "&:hover": { backgroundColor: "rgb(245, 245, 245)" },
  },
  "& .MuiDataGrid-cell": {
    borderBottom: "1px solid #F1F5F9",
    display: "flex",
    alignItems: "center",
    outline: "none !important",
    padding: "0 24px",
  },
  "& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within": {
    outline: "none",
  },
  "& .MuiDataGrid-footerContainer": {
    border: "1px solid #E2E8F0",
    backgroundColor: "#fff",
    borderRadius: "0 0 15px 15px",
    flexShrink: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    minHeight: "40px",
    padding: "0 8px",
  },
  "& .MuiPaginationItem-root": { borderRadius: "4px" },
  "& .MuiDataGrid-main": { flex: 1, minHeight: 0, overflow: "hidden" },
  "& .MuiDataGrid-virtualScroller": { overflowY: "auto !important", flex: 1 },
};

// ── Main Component ────────────────────────────────────────────────────────────
const RecommendedTab = (props) => {
  const { id } = useParams();
  const [recommendationLoading, setRecommendationLoading] = useState(
    () => localStorage.getItem(`topRec_${id}`) === "true",
  );
  const PermissionStatus = useSelector((state) => state.user.permissions);
  const CandidatePermission = PermissionStatus["100"];

  // ── Search
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const debounceRef = useRef(null);

  // ── Table
  const [applicants, setApplicantsdata] = useState([]);
  const [showLoader, setShowLoader] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 20,
  });
  const [queryOptions, setQueryOptions] = useState({});
  const [rerenderKey, setRerenderKey] = useState(0);
  const [shimmer, setshimmer] = useState(false);
  const [showCustomMessage, setShowCustomMessage] = useState(false);

  // ── Candidate drawer
  const [isApplicanOpen, setApplicanOpen] = useState(false);
  const [selectedRowData, setSelectedRowData] = useState(null);
  const [verifiedstatus, setverifiedstatus] = useState("");
  const [opennew, setOpennew] = useState(false);
  const [refreshagain, setrefreshagain] = useState(false);
  const [activitydisable, setactivitydisable] = useState(true);
  const [candidatedrawerflag] = useState(true);

  // ── Selection
  const [checkboxSelected] = useState(true);
  const [addToJobCandidateSelection, setAddToJobCandidateSelection] = useState(
    [],
  );
  const [addtojobcount, setaddtojobcount] = useState(true);
  const [warning, setwarning] = useState(false);
  const [isAnyCheckboxSelected, setIsAnyCheckboxSelected] = useState(false);
  const [keepSelected, setKeepSelected] = useState(true);
  const [adddisbale, setadddisable] = useState(false);
  const [bulkdata, setbulkdata] = useState([]);
  const [addtoJob, setAddtoJob] = useState(false);

  // ── Snackbar / Dialog
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [open, setOpen] = useState(false);

  // ── Advance Filter
  const [FilterHitting, setFilterHitting] = useState(false);
  const [advanceFilter, setAdvanceFilter] = useState(false);
  const [question] = useState([]);
  const [quesAnsArrayFilter, setQuesAnsArrayFilter] = useState([]);
  const [current, setCurrent] = useState([]);
  const [textFields, setTextFields] = useState({});
  const [radioValues, setRadioValues] = useState({});
  const [operatorValues, setOperatorValues] = useState({});
  const [checkboxValues, setCheckboxValues] = useState({});
  const [checkedCount, setCheckedCount] = useState(0);
  const [selectedCount, setSelectedCount] = useState(0);
  const [selectedOptions, setSelectedOptions] = useState([]);
  const [candidateList, setCandidateList] = useState([]);


  const [createdby, setCreatedby] = useState("");
  const [newData, setNewData] = useState([]);
  const [cancelData, setCancelData] = useState(false);
  const [username, setUsername] = useState("");

  const [filterOptions, filtersetQueryOptions] = useState({
    filterModel: null,
  });

  const containerRef = useRef(null);

  // ── Get user data ─────────────────────────────────────────────────────────
  const getUserData = async () => {
    const user_id = localStorage.getItem("user_id");
    const candidateOwnerList = await axios.get(`active-users/${user_id}`, {
      headers: {
        authorization:
          Cookies.get("_secure_ARJ_") || Cookies.get("_secure_ref"),
      },
    });
    setCandidateList(candidateOwnerList.data.message);
    setUsername("Select User Name");
  };

  useEffect(() => {
    getUserData();
  }, []);

  useEffect(() => {
    const stored = localStorage.getItem(`topRec_${id}`) === "true";
    setRecommendationLoading(stored);
    if (stored) {
      setApplicantsdata([]);
      setShowLoader(true);
      setQueryOptions({ sortModel: [{ field: "score", sort: "desc" }] });
      setPaginationModel((prev) => ({ ...prev, page: 0 }));
      fetchTopRecommendations().finally(() => {
        setrefreshagain((prev) => !prev);
      });
    }
  }, [id]);

  useEffect(() => {
    const timeoutId = setTimeout(() => setShowCustomMessage(true), 2000);
    return () => clearTimeout(timeoutId);
  }, []);

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  // ── Main API Effect ───────────────────────────────────────────────────────
  useEffect(() => {
    if (recommendationLoading) {
      setShowLoader(true);
      return;
    }

    setShowLoader(true);
    const payload = {
      jobId: id,
      total_rows: paginationModel.pageSize,
      filterCriteria: createdby?.includes("candidate_info.entered_by in ()")
        ? ""
        : createdby || "",
    };
    if (queryOptions.sortModel?.length > 0) {
      payload.sortField = queryOptions.sortModel[0].field;
      payload.sortBy = queryOptions.sortModel[0].sort;
    }
    axios
      .post(
        `ai-recommendation/candidate-list/${paginationModel.page + 1}${debouncedSearch ? "?search=" + debouncedSearch : ""}`,
        payload,
        {
          headers: {
            authorization:
              Cookies.get("_secure_ARJ_") || Cookies.get("_secure_ref"),
          },
        },
      )
      .then((response) => {
        const filtered = response.data.message.data.filter(
          (a) => a.id !== null,
        );
        setApplicantsdata(filtered);
        setTotalCount(response.data.message.page);
        setShowLoader(false);
      })
      .catch((err) => {
        setShowLoader(false);
        setApplicantsdata([]);
        console.error(err);
      });
  }, [
    paginationModel,
    queryOptions,
    debouncedSearch,
    shimmer,
    isApplicanOpen,
    filterOptions,
    createdby, // sirf Apply/Reset par change hoga
    refreshagain,
    recommendationLoading,
  ]);

  // ── Filter handlers ───────────────────────────────────────────────────────

  // ✅ Sirf UI update — API nahi
  const handleSelectionData = (event, value, reason) => {
    setSelectedOptions(value);
    setSelectedCount(value.length);
    if (reason === "clear" || reason === "removeOption") {
      setCancelData(true);
    }
  };

  const handleCheckboxChange = (e, id, index) => {
    const checked = e.target.checked;
    setCheckboxValues((prev) => ({ ...prev, [id]: checked }));
    let tempArr = [...quesAnsArrayFilter];
    tempArr[index].isChecked = checked;
    if (!checked) {
      tempArr[index].ans_text = "";
      tempArr[index].errorText = null;
      setTextFields((prev) => ({ ...prev, [id]: "" }));
      setRadioValues((prev) => ({ ...prev, [id]: "" }));
    }
    setQuesAnsArrayFilter(tempArr);
    setCheckedCount(tempArr.filter((i) => i.isChecked).length);
  };

  const handleOperatorValuesChange = (e, id, index) => {
    setOperatorValues((prev) => ({ ...prev, [id]: e.target.value }));
    let tempArr = [...quesAnsArrayFilter];
    tempArr[index].operator = e.target.value;
    setQuesAnsArrayFilter(tempArr);
  };

  const handleTextFieldChange = (e, id, index) => {
    const capitalizedValue =
      e.target.value.charAt(0).toUpperCase() + e.target.value.slice(1);
    setTextFields((prev) => ({ ...prev, [id]: capitalizedValue }));
    let tempArr = [...quesAnsArrayFilter];
    if (tempArr[index].isChecked) {
      tempArr[index].ans_text = capitalizedValue;
      tempArr[index].operator = quesAnsArrayFilter[index].operator || "=";
    }
    setQuesAnsArrayFilter(tempArr);
  };

  const handleRadioChange = (e, id, index) => {
    setRadioValues((prev) => ({ ...prev, [id]: e.target.value }));
    let tempArr = [...quesAnsArrayFilter];
    if (tempArr[index].isChecked) {
      tempArr[index].ans_text = e.target.value;
      tempArr[index].operator = quesAnsArrayFilter[index].operator || "";
    }
    setQuesAnsArrayFilter(tempArr);
  };

  const handleAdvanceFilterOpen = () => {
    setNewData(selectedOptions);
    let mytemp = current.map((q) => ({ ...q }));
    const updatedText = {},
      updatedRadio = {},
      updatedOperator = {},
      updatedChecked = {};
    mytemp.forEach((q) => {
      updatedText[q.question_id] = q.ans_text;
      updatedRadio[q.question_id] = q.ans_text;
      updatedOperator[q.question_id] = q.operator;
      if (q.isChecked !== undefined)
        updatedChecked[q.question_id] = q.isChecked;
    });
    setTextFields((prev) => ({ ...prev, ...updatedText }));
    setRadioValues((prev) => ({ ...prev, ...updatedRadio }));
    setOperatorValues((prev) => ({ ...prev, ...updatedOperator }));
    setCheckboxValues((prev) => ({ ...prev, ...updatedChecked }));
    setQuesAnsArrayFilter([...mytemp]);
    if (cancelData) setSelectedCount(newData.length);
    setAdvanceFilter(true);
  };

  const handleAdvanceFilterClose = () => {
    setAdvanceFilter(false);
    setSelectedOptions(selectedOptions.length > 0 || cancelData ? newData : []);
  };

  const validate = () => {
    let isValid = true;
    let updateArray = [...quesAnsArrayFilter];
    updateArray.forEach((item) => {
      if (item.type_id === 2 && item.isChecked) {
        if (!item.ans_text) {
          item.errorText = "This field is required";
          isValid = false;
        } else if (item.ans_text.length > 12) {
          item.errorText = "Maximum 12 digits allowed";
          isValid = false;
        } else item.errorText = null;
      } else if (item.type_id === 3 && item.isChecked) {
        if (!item.ans_text) {
          item.errorText = "Please select an option";
          isValid = false;
        } else item.errorText = null;
      } else item.errorText = null;
    });
    setQuesAnsArrayFilter(updateArray);
    return isValid;
  };

  // ✅ Apply button par hi API call
  const handleAdvanceFilterApply = () => {
    if (!validate()) return;

    let error = 0;
    let updateArray = [...quesAnsArrayFilter];
    updateArray.forEach((item) => {
      if (item.isChecked) {
        if (item.type_id === 2 && !item.ans_text) {
          error = 1;
          item.errorText = "This field is required";
        } else if (item.type_id === 3 && !item.ans_text) {
          error = 1;
          item.errorText = "Please select an option";
        }
      }
    });

    if (!error) {
      // ✅ Apply par createdby set karo
      const usernames = selectedOptions?.map((item) => item.name);
      const filterCondition = generate_filter_condition(
        " candidate_info.entered_by ",
        "isAnyOf",
        usernames,
      );
      setCreatedby(usernames.length > 0 ? filterCondition : "");

      let mytemp = quesAnsArrayFilter.map((q) => ({ ...q }));
      const countChecked = quesAnsArrayFilter.filter((i) => i.isChecked).length;
      setCheckedCount(
        selectedOptions.length || newData.length > 0
          ? countChecked + 1
          : countChecked,
      );
      setCurrent([...mytemp]);
      setFilterHitting(true);
      setAdvanceFilter(false);
      setrefreshagain((prev) => !prev); // ✅ API trigger
    }
  };

  // ✅ Reset button par API call
  const handleFilterReset = () => {
    setTextFields({});
    setRadioValues({});
    setCheckboxValues({});
    setCheckedCount(0);
    const a = quesAnsArrayFilter.map((q) => ({
      type_id: q.type_id,
      question_id: q.question_id,
      ans_text: "",
      errorText: null,
      operator: "=",
      isChecked: false,
    }));
    setQuesAnsArrayFilter(a);
    setCurrent(a);
    setAdvanceFilter(false);
    setFilterHitting(false);
    setSelectedOptions([]);
    setCreatedby(""); // ✅ Reset par clear
    setrefreshagain((prev) => !prev); // ✅ API trigger
  };

  const handleSortModelChange = React.useCallback((sortModel) => {
    setQueryOptions({ sortModel: [...sortModel] });
  }, []);

  const handleSnackbarOpen = (message) => {
    setSnackbarMessage(message);
    setOpenSnackbar(true);
  };
  const handleSnackbarClose = () => setOpenSnackbar(false);
  const handleCloseDialog = () => setOpenDialog(false);

  const onFilterChange = React.useCallback((filterModel) => {
    const respFilterData = onGlobalFliterFunc(
      filterModel,
      `and concat(u.first_name, ' ', u.last_name)`,
    );
    if (respFilterData) filtersetQueryOptions(respFilterData);
  }, []);

  const HandleRowClick = (params) => {
    setverifiedstatus(params.row.isVerified);
    setSelectedRowData(params.row);
    setApplicanOpen(true);
  };

  const handleSelection = (params) => {
    if (params.length < 1) setaddtojobcount(true);
    if (params.length > 10) {
      setwarning(true);
      containerRef.current?.scrollTo({ top: 0, behavior: "smooth" });
      setAddToJobCandidateSelection(params);
      setaddtojobcount(false);
    } else if (params.length > 0) {
      setAddToJobCandidateSelection(params);
      setIsAnyCheckboxSelected(true);
      setaddtojobcount(false);
      setwarning(false);
    }
  };

  const filteredApplicants = applicants.filter((row) =>
    Object.values(row).some((v) =>
      v?.toString().toLowerCase().includes(searchTerm.toLowerCase()),
    ),
  );
  const isInactiveJob =
    props.status === "Closed" ||
    props.status === "Cancelled" ||
    props.status === "Full" ||
    props.status === "On Hold";

  const handleOnClose = () => {
    setApplicanOpen(false);
    setOpennew((prev) => !prev);
  };

  const handleAddCandidates = () => {
    setshimmer(true);
    axios
      .post(
        `bulk-add/job`,
        {
          candidate_ids: addToJobCandidateSelection || [],
          job_id: id,
          quesAnsArray: [],
          isShortlisted: 0,
        },
        {
          headers: {
            authorization:
              Cookies.get("_secure_ARJ_") || Cookies.get("_secure_ref"),
          },
        },
      )
      .then((response) => {
        if (response.status === 200) {
          setKeepSelected(!keepSelected);
          setRerenderKey((prev) => prev + 1);
          setAddToJobCandidateSelection([]);
          setaddtojobcount(true);
          setshimmer(false);
          setwarning(false);
          setbulkdata(response.data.message.details);
          if (response.data.message.failure_candidates >= 1)
            setadddisable(true);
          handleSnackbarOpen("Candidate Added Successfully");
          setRecommendationLoading(false);
          setAddtoJob(response.data.message.failure_candidates !== 0);
          if (response.data.message.failure_candidates === 0)
            setOpenSnackbar(true);
        }
      })
      .catch((err) => {
        setRecommendationLoading(false);
        handleSnackbarOpen(err.response.data.error);
      });
  };

  const fetchTopRecommendations = async () => {
    try {
      await axios.post(
        `/top-recommendations`,
        { jobId: id, operation: 0 },
        {
          headers: {
            authorization:
              Cookies.get("_secure_ARJ_") || Cookies.get("_secure_ref"),
          },
        },
      );
    } catch (err) {
      console.error("Top recommendation error:", err);
    } finally {
      setRecommendationLoading(false);
      localStorage.removeItem(`topRec_${id}`);
    }
  };

  const handleTopRecommendations = async () => {
    setApplicantsdata([]);
    setRecommendationLoading(true);
    localStorage.setItem(`topRec_${id}`, "true");
    setShowLoader(true);
    await fetchTopRecommendations();
    setRerenderKey((prev) => prev + 1);
    setPaginationModel({ ...paginationModel, page: 0 });
    containerRef.current?.scrollTo({ top: 0, behavior: "smooth" });
    setQueryOptions({ sortModel: [{ field: "score", sort: "desc" }] });
    setrefreshagain((prev) => !prev);
  };

  return (
    <Box
      ref={containerRef}
      sx={{
        position: "relative",
        display: "flex",
        flexDirection: "column",
        height: "100%",
        gap: 2,
      }}
    >
      {/* ── Toolbar ── */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Typography variant="h6" fontWeight={700} sx={{ fontSize: "16px" }}>
          Applicants
        </Typography>

        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <TextField
            size="small"
            type="search"
            placeholder="Search"
            variant="outlined"
            value={searchTerm}
            onChange={(e) => {
              const value = e.target.value;
              setSearchTerm(value);
              if (debounceRef.current) clearTimeout(debounceRef.current);
              debounceRef.current = setTimeout(() => {
                setDebouncedSearch(value);
              }, 500);
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" sx={{ color: "#667085" }} />
                </InputAdornment>
              ),
              sx: {
                borderRadius: "8px",
                fontSize: "0.875rem",
                height: "32px",
                backgroundColor: "background.paper",
              },
            }}
            sx={{ width: "200px" }}
          />

          {CandidatePermission?.add === 1 && (
            <Button
              variant="outlined"
              size="small"
              onClick={handleAddCandidates}
              disabled={addtojobcount || warning}
              sx={{
                borderRadius: "8px",
                textTransform: "none",
                fontWeight: 600,
                fontSize: "13px",
                height: "32px",
              }}
            >
              ADD TO JOB
            </Button>
          )}

          <Tooltip
            title={
              recommendationLoading
                ? "Top Recommendations is processing..."
                : ""
            }
            arrow
            placement="top"
          >
            <span>
              <Button
                variant="outlined"
                size="small"
                startIcon={<img src="/assets/top_rec.svg" alt="" />}
                disabled={recommendationLoading}
                onClick={handleTopRecommendations}
                sx={{
                  borderRadius: "8px",
                  borderColor: "#EA08C4",
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: "13px",
                  height: "31px",
                  color: "#EA08C4",
                  "&:hover": {
                    borderColor: "#ef12ca",
                    backgroundColor: "#fffafd",
                  },
                }}
              >
                REFRESH
              </Button>
            </span>
          </Tooltip>

          <Button
            variant="outlined"
            size="small"
            onClick={handleAdvanceFilterOpen}
            startIcon={<img src="/assets/filter.svg" alt="" />}
            sx={{
              textTransform: "none",
              fontWeight: 500,
              fontSize: "0.85rem",
              height: "32px",
              borderRadius: "8px",
              borderColor: "divider",
              color: "text.primary",
              backgroundColor: "background.paper",
              "&:hover": {
                borderColor: "text.secondary",
                backgroundColor: "action.hover",
              },
              gap: 0.5,
              "& .MuiButton-startIcon": { marginRight: 0 },
            }}
          >
            FILTER
            {checkedCount > 0 && (
              <Box
                component="span"
                sx={{
                  ml: 0.5,
                  backgroundColor: "#1976d2",
                  color: "white",
                  borderRadius: "50px",
                  px: "7px",
                  py: "1px",
                  fontSize: "12px",
                  fontWeight: 600,
                  lineHeight: 1.5,
                }}
              >
                {checkedCount}
              </Box>
            )}
          </Button>
        </Box>
      </Box>

      {advanceFilter && (
        <Box sx={{ position: "absolute", top: "48px", right: 0, zIndex: 1200 }}>
          <AdvanceFilter
            advanceFilter={advanceFilter}
            handleAdvanceFilterOpen={handleAdvanceFilterOpen}
            handleAdvanceFilterClose={handleAdvanceFilterClose}
            handleAdvanceFilterApply={handleAdvanceFilterApply}
            handleFilterReset={handleFilterReset}
            question={question}
            textFields={textFields}
            radioValues={radioValues}
            selectedCount={selectedCount}
            selectedOptions={selectedOptions}
            handleSelectionData={handleSelectionData}
            candidateList={candidateList}
            username={username}
          />
        </Box>
      )}

      {warning && (
        <Typography sx={{ mb: 1, mt: 1 }} color="error">
          You cannot select more than 10 Candidates.
        </Typography>
      )}

      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          width: "100%",
          height: "100%",
          overflowY: "hidden",
          borderRadius: "15px",
        }}
      >
        <DataGrid
          rows={showLoader ? [] : filteredApplicants}
          columns={columns}
          key={rerenderKey}
          loading={showLoader}
          paginationMode="server"
          getRowId={(row) => row.id}
          onRowClick={HandleRowClick}
          checkboxSelection={
            isInactiveJob ? false : checkboxSelected
          }
          keepNonExistentRowsSelected
          disableColumnFilter
          disableColumnMenu
          paginationModel={paginationModel}
          onPaginationModelChange={(newModel) => {
            setPaginationModel(newModel);
            containerRef.current?.scrollTo({ top: 0, behavior: "smooth" });
          }}
          pageSizeOptions={[20, 25, 50]}
          rowCount={totalCount}
          sortingMode="server"
          filterMode="server"
          localeText={
            showCustomMessage ? { noRowsLabel: "No Rows" } : { noRowsLabel: "" }
          }
          onFilterModelChange={_debounce(onFilterChange, 500)}
          onSortModelChange={handleSortModelChange}
          onRowSelectionModelChange={handleSelection}
          disableRowSelectionOnClick
          getRowClassName={(params) =>
            params.indexRelativeToCurrentPage % 2 === 1 ? "row-alternate" : ""
          }
          slots={{
            pagination: () => (
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 2, px: 2 }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography
                    variant="body2"
                    sx={{ color: "rgba(0,0,0,0.6)", fontSize: "14px" }}
                  >
                    Rows per page:
                  </Typography>
                  <select
                    value={paginationModel.pageSize}
                    onChange={(e) =>
                      setPaginationModel((prev) => ({
                        ...prev,
                        pageSize: Number(e.target.value),
                        page: 0,
                      }))
                    }
                    style={{
                      border: "1px solid #E2E8F0",
                      borderRadius: "4px",
                      padding: "2px 6px",
                      fontSize: "14px",
                      cursor: "pointer",
                      outline: "none",
                    }}
                  >
                    {[20, 25, 50].map((size) => (
                      <option key={size} value={size}>
                        {size}
                      </option>
                    ))}
                  </select>
                </Box>
                <Pagination
                  count={Math.ceil(totalCount / paginationModel.pageSize) || 1}
                  page={paginationModel.page + 1}
                  onChange={(e, page) =>
                    setPaginationModel((prev) => ({ ...prev, page: page - 1 }))
                  }
                  shape="rounded"
                  color="primary"
                  siblingCount={1}
                  boundaryCount={1}
                  size="small"
                />
              </Box>
            ),
            noRowsOverlay: () => (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  height: "100%",
                  gap: 1,
                  color: "#94a3b8",
                }}
              >
                <PersonOffOutlinedIcon sx={{ fontSize: "48px" }} />
                <Typography sx={{ fontSize: "14px", fontWeight: 500 }}>
                  {isInactiveJob
                    ? "Recommendations are available only for jobs that are currently active."
                    : "No Candidate Found"}
                </Typography>
              </Box>
            ),
          }}
          sx={dataGridSx}
        />
      </Box>

      <CandidateDetails
        activitydisable={activitydisable}
        setactivitydisable={setactivitydisable}
        verifiedstatus={verifiedstatus}
        candidatedrawerflag={candidatedrawerflag}
        isOpen={isApplicanOpen}
        setApplicanOpen={setApplicanOpen}
        onClose={handleOnClose}
        opennew={opennew}
        setOpennew={setOpennew}
        selectedRowData={selectedRowData}
        setrefreshagain={setrefreshagain}
        refreshagain={refreshagain}
        issearchnavbar={false}
        questionrequired={props.questionrequired}
        Jobstatus={props.status}
      />

      <Dialog open={openDialog} onClose={handleCloseDialog}>
        <DialogTitle sx={{ color: "red" }}>Error</DialogTitle>
        <DialogContent>
          You cannot select more than 10 Candidates.
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Close</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        open={openSnackbar}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={
            snackbarMessage === "Candidate Added Successfully"
              ? "success"
              : "error"
          }
          sx={{ width: "100%" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>

      <BootstrapDialog
        onClose={() => setOpen(false)}
        aria-labelledby="customized-dialog-title"
        open={open}
      >
        <DialogTitle sx={{ m: 0, p: 2 }} id="customized-dialog-title">
          Required additional questions
        </DialogTitle>
        <IconButton
          aria-label="close"
          onClick={() => setOpen(false)}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
        <DialogContent dividers>
          <Typography gutterBottom>
            This job requires screening questions to be answered. We suggest you
            to add candidates one by one for efficient screening.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button autoFocus onClick={() => setOpen(false)} color="error">
            Close
          </Button>
        </DialogActions>
      </BootstrapDialog>
    </Box>
  );
};

export default RecommendedTab;
